/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Run syncs in a sequence", description = "Chain dependent syncs into a sequence so they run in order, with per-sync rules for when a failure should stop the rest.", lastUpdatedDate = new Date(1787184000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Run syncs in a sequence",
        description: "Chain dependent syncs into a sequence so they run in order, with per-sync rules for when a failure should stop the rest.",
        lastUpdatedDate: "Thu Aug 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Why use a sequence",
          "depth": 0,
          "id": "why-use-a-sequence"
        }, {
          "value": "Before you begin",
          "depth": 0,
          "id": "before-you-begin"
        }, {
          "value": "Create a sequence",
          "depth": 0,
          "id": "create-a-sequence"
        }, {
          "value": "Control what happens when a sync fails",
          "depth": 0,
          "id": "control-what-happens-when-a-sync-fails"
        }, {
          "value": "Run and monitor a sequence",
          "depth": 0,
          "id": "run-and-monitor-a-sequence"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      h2: "h2",
      ol: "ol",
      li: "li",
      img: "img",
      ul: "ul"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Sequences"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "A sequence runs a set of related syncs in a fixed order, so a sync that depends on another only runs after that one finishes."
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams orchestrating dependent syncs"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["Two or more syncs, each set to the ", _jsx(_components.a, {
                href: "/syncs/schedule-sync-ui#manual-runs",
                children: "Manual"
              }), " schedule type."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "why-use-a-sequence",
        children: "Why use a sequence"
      }), "\n", _jsx(_components.p, {
        children: "Use a sequence when one sync depends on another finishing first. For example, you might have two syncs that must run in order:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Sync A creates a set of new contacts in your CRM."
        }), "\n", _jsx(_components.li, {
          children: "Sync B adds those contacts to their accounts."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Without a sequence, you'd schedule the two syncs far enough apart to hope the first finishes before the second starts. Sync duration varies, so that timing is never guaranteed. A sequence removes the guesswork: each sync starts only after the previous one finishes, and you decide per sync whether a failure should stop the rest."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sequences-flow-chart.png",
          alt: "A sequence of four dependent syncs"
        })
      }), "\n", _jsx(_components.h2, {
        id: "before-you-begin",
        children: "Before you begin"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Have two or more syncs in your workspace. A sequence needs at least two syncs to order."
        }), "\n", _jsxs(_components.li, {
          children: ["Set each sync you'll add to the ", _jsx(_components.a, {
            href: "/syncs/schedule-sync-ui#manual-runs",
            children: "Manual"
          }), " schedule type. A sync's own schedule runs independently of any sequence, so a sync left on an interval or cron schedule runs both on that schedule and as part of the sequence."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "create-a-sequence",
        children: "Create a sequence"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsx(_components.a, {
            href: "https://app.hightouch.com/sequences?from=docs",
            children: _jsx(_components.strong, {
              children: "Sequences"
            })
          }), " page and click ", _jsx(_components.strong, {
            children: "Create sequence"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Add syncs"
          }), " and select the syncs to include. You can add more later."]
        }), "\n", _jsx(_components.li, {
          children: "Reorder the syncs with the up and down arrows so they run in the order you need."
        }), "\n", _jsxs(_components.li, {
          children: ["Set each sync's failure behavior with its ", _jsx(_components.strong, {
            children: "Terminate"
          }), " menu (see ", _jsx(_components.a, {
            href: "#control-what-happens-when-a-sync-fails",
            children: "Control what happens when a sync fails"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Name the sequence and set its schedule. A sequence supports the same ", _jsx(_components.a, {
            href: "/syncs/schedule-sync-ui#schedule-types",
            children: "schedule types"
          }), " as a sync."]
        }), "\n", _jsx(_components.li, {
          children: "Save the sequence."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sequences/configure-sequence.png",
          alt: "A sequence with its syncs ordered and each sync's Terminate behavior set"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The final step is where you name the sequence and choose its ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-ui#schedule-types",
          children: "schedule type"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sequences/schedule-type.png",
          alt: "Choosing a schedule type for the sequence"
        })
      }), "\n", _jsx(_components.h2, {
        id: "control-what-happens-when-a-sync-fails",
        children: "Control what happens when a sync fails"
      }), "\n", _jsxs(_components.p, {
        children: ["Each sync in the sequence has a ", _jsx(_components.strong, {
          children: "Terminate"
        }), " menu that decides whether that sync's outcome stops the rest of the sequence. Under ", _jsx(_components.strong, {
          children: "Terminate sequence when"
        }), ", choose any combination of:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync run has fatal errors"
          }), " — stop the sequence if the sync can't complete."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync run has rejected rows"
          }), " — stop the sequence if the sync's rejected-row rate exceeds the ", _jsx(_components.strong, {
            children: "Error rate threshold"
          }), " you set, as a percentage of the run's rows."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Leave both unchecked (the menu shows ", _jsx(_components.strong, {
          children: "No"
        }), ") and the sequence continues to the next sync no matter how this one turns out."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sequences/terminate-options.png",
          alt: "The Terminate menu with rejected-rows and fatal-errors options"
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "When a sync terminates the sequence, the syncs after it don't run. Order your\nsyncs so a failure stops the sequence before any dependent sync runs on\nincomplete data."
        })
      }), "\n", _jsx(_components.h2, {
        id: "run-and-monitor-a-sequence",
        children: "Run and monitor a sequence"
      }), "\n", _jsxs(_components.p, {
        children: ["Open a sequence from the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/sequences?from=docs",
          children: _jsx(_components.strong, {
            children: "Sequences"
          })
        }), " page to reach its detail page, where you can:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Enable or disable it."
        }), "\n", _jsx(_components.li, {
          children: "Run it manually."
        }), "\n", _jsx(_components.li, {
          children: "Review its run history."
        }), "\n", _jsx(_components.li, {
          children: "Edit its configuration or schedule."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sequences/run-history.png",
          alt: "A sequence's detail page showing its enable toggle, Run button, and run history"
        })
      }), "\n", _jsx(_components.p, {
        children: "To confirm a run did what you expected, click it to see each member sync's execution time and final state. A sync that terminated the sequence shows here, along with any syncs that were skipped as a result."
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
